import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ThemeProvider } from '@material-ui/core/styles';

import { Theme } from '../theme';
import { Icons } from '../icons/icons';
import { Unicons } from '../icons/unicons';
import { StyledTextField, StyledTextFieldIcon } from '../mui/mui-textfield';

// Material-UI Pickers
import { DatePicker, KeyboardDatePicker } from '@material-ui/pickers';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';

import moment from 'moment';
import {
  LocalizedUtils,
  createPickerTheme,
  getPickerLanguage,
  getPickerLocale,
  setPickerMomentLocale
} from './mui-pickers.utils';

// TextField DatePicker
const MuiTextField = ({ ...props }) => {
  return (
    <StyledTextField
      {...props}
      InputProps={{ endAdornment: (<Unicons.Calendar size="20" style={{ minHeight: '20px', minWidth: '20px' }} />) }}
    />
  );
};

// TextFieldIcon DatePicker
const MuiTextFieldIcon = ({ ...props }) => {
  return (
    <StyledTextFieldIcon
      {...props}
      InputProps={{ startAdornment: (<Icons.FilterList />), endAdornment: (<Unicons.Calendar size="20" style={{ minHeight: '20px', minWidth: '20px' }} />) }}
    />
  );
};

// Formats
// dd/mm/yyyy -> lu/09/2020
// DD/mm/yyyy -> 21/09/2020
// LL -> 21 de septiembre de 2020
// dddd LL -> viernes 21 de septiembre de 2020

const materialTheme = createPickerTheme({
  MuiPickersToolbar: {
    toolbar: {
      height: '70px',
      backgroundColor: Theme.colors.blueDark,
      '& h4': {
        fontFamily: 'Bryant Pro Medium',
        fontSize: Theme.typography.heading4,
        fontWeight: 'normal',
        letterSpacing: 'normal',
        whiteSpace: 'normal',
      },
      '& h6': {
        fontFamily: 'Bryant Pro Medium',
        fontSize: Theme.typography.heading6,
        fontWeight: 'normal',
        letterSpacing: '0.0175em',
        whiteSpace: 'normal',
      },
    }
  }
});

const MuiDatePicker = (props) => {
  const lang = getPickerLanguage();
  const locale = getPickerLocale(lang);

  const {
    allowKeyboard,
    mask,
    isFilter,
    value,
    onChange,
    ...restProps
  } = props;

  // Changes value after closing it.
  const [isOpen, setIsOpen] = useState(false);
  const [internalDate, setInternalDate] = useState(value);

  const tempDateRef = useRef(value); // Use ref to keep track of tempDate.

  useEffect(() => {
    setPickerMomentLocale(lang);
  }, [lang]);

  useEffect(() => {
    setInternalDate(value);
    tempDateRef.current = value;
  }, [value]);

  const onChangeHandler = (val) => {
    tempDateRef.current = val; // Update ref with the latest value
  };

  const handleClose = () => {
    setIsOpen(false);
    setInternalDate(tempDateRef.current); // Use the ref value
    if (typeof onChange === 'function') {
      onChange(tempDateRef.current); // Call 'onChange' with the ref value
    }
  };

  return (
    <ThemeProvider theme={materialTheme}>
      <MuiPickersUtilsProvider utils={LocalizedUtils} locale={locale} moment={moment}>
        {!allowKeyboard ? (
          <DatePicker
            {...restProps}
            autoOk
            variant="inline"
            mask={mask ? mask : '__/__/____'}
            leftArrowIcon={<Icons.ArrowLeft />}
            rightArrowIcon={<Icons.ArrowRight />}
            TextFieldComponent={!isFilter ? MuiTextField : MuiTextFieldIcon}
            value={tempDateRef.current}
            onChange={onChangeHandler}
            open={isOpen}
            onOpen={() => {
              setIsOpen(true);
              tempDateRef.current = internalDate; // Reset ref to the current value when opening
            }}
            onClose={handleClose} // Call handleClose to update the main value
          />
        ) : (
          <KeyboardDatePicker
            {...restProps}
            autoOk
            variant="inline"
            mask={mask ? mask : '__/__/____'}
            leftArrowIcon={<Icons.ArrowLeft />}
            rightArrowIcon={<Icons.ArrowRight />}
            TextFieldComponent={StyledTextField}
            value={tempDateRef.current}
            onChange={onChangeHandler}
            open={isOpen}
            onOpen={() => {
              setIsOpen(true);
              tempDateRef.current = internalDate; // Reset ref to the current value when opening
            }}
            onClose={handleClose} // Call handleClose to update the main value
          />
        )}
      </MuiPickersUtilsProvider>
    </ThemeProvider>
  );
};

MuiDatePicker.propTypes = {
  // Boolean to change DatePicker to KeyboardDatePicker.
  allowKeyboard: PropTypes.bool,
  // Mask to keyboard.
  mask: PropTypes.string,
  // Boolean to show filter icon in TextField.
  isFilter: PropTypes.bool,
  // The value of the DatePicker.
  value: PropTypes.oneOfType([PropTypes.any, PropTypes.string, PropTypes.array]),
  // Callback fired when the value changes.
  onChange: PropTypes.func
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

/* KeyboardDatePicker Styles */
export const StyledDatePicker = styled(MuiDatePicker)`
  .MuiIconButton-root {
    padding: 8px;
    right: -8px;
    color: ${(props) => props.theme.colors.greyDark};
  }
`;
